import jsonLogic, { type RulesLogic } from 'json-logic-js';
import { searchJmesPath } from './jmespath';

const isEmptyValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

jsonLogic.add_operation('empty', isEmptyValue);
jsonLogic.add_operation('notEmpty', (value: unknown) => !isEmptyValue(value));
jsonLogic.add_operation('required', (value: unknown) => !isEmptyValue(value));

export const applyJsonLogic = (rule: RulesLogic, data: unknown): any =>
  jsonLogic.apply(rule, data);

const SCOPE_OPERATORS = new Set([
  'some',
  'all',
  'none',
  'filter',
  'map',
  'reduce',
  'empty',
  'notEmpty',
]);

const isRootVarPath = (path: unknown): path is string =>
  typeof path === 'string' && path.startsWith('$');

const resolveRootVar = (varArg: unknown, rootContext: unknown): unknown => {
  const [path, fallback] = Array.isArray(varArg) ? varArg : [varArg];
  return searchJmesPath(rootContext, path as string) ?? fallback ?? null;
};

/**
 * Inlines every `$`-prefixed root var found anywhere within `node` as a resolved
 * literal, since `json-logic-js` replaces `data` entirely with the current loop
 * item inside `some`/`all`/`none`/`filter`/`map`/`reduce`, making root vars
 * otherwise unreachable once inside one of those operators.
 */
const inlineRootVars = (node: unknown, rootContext: unknown): unknown => {
  if (Array.isArray(node)) {
    return node.map(item => inlineRootVars(item, rootContext));
  }

  if (node && typeof node === 'object') {
    const [op, args] = Object.entries(node)[0] ?? [];

    if (op === 'var') {
      const path = Array.isArray(args) ? args[0] : args;
      return isRootVarPath(path) ? resolveRootVar(args, rootContext) : node;
    }

    return { [op as string]: inlineRootVars(args, rootContext) };
  }

  return node;
};

/**
 * Walks a JsonLogic rule and, for the child logic of any `some`/`all`/`none`/
 * `filter`/`map`/`reduce` operator, resolves `$`-prefixed vars (e.g. `$actor.id`)
 * against `rootContext` via JMESPath and inlines them as literals. This lets
 * root-scoped vars keep working once `json-logic-js` swaps `data` for the
 * current array item. Vars outside these operators are left untouched.
 */
export const resolveScopedRootVars = (
  rule: RulesLogic,
  rootContext: unknown
): RulesLogic => {
  if (Array.isArray(rule)) {
    return rule.map(item =>
      resolveScopedRootVars(item, rootContext)
    ) as unknown as RulesLogic;
  }

  if (rule && typeof rule === 'object') {
    const [op, args] = Object.entries(rule)[0] ?? [];

    if (op && SCOPE_OPERATORS.has(op) && Array.isArray(args)) {
      const [arrayArg, childLogic, ...rest] = args;
      return {
        [op]: [
          resolveScopedRootVars(arrayArg, rootContext),
          inlineRootVars(childLogic, rootContext),
          ...rest.map(item => resolveScopedRootVars(item, rootContext)),
        ],
      } as RulesLogic;
    }

    return {
      [op as string]: resolveScopedRootVars(args, rootContext),
    } as RulesLogic;
  }

  return rule;
};
